import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Pause, Sparkle, ArrowsClockwise, FloppyDisk, FilmSlate, TrashSimple, Lock, Microphone, Megaphone, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import ShareVoiceSheet from "../components/ShareVoiceSheet";

/** AI Voice Studio — ElevenLabs text-to-speech: pick a voice, generate, preview, save, use in videos. */
export default function AIVoiceStudio() {
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [text, setText] = useState("");
  const [voiceId, setVoiceId] = useState("EXAVITQu4vr4xnSDxMaL");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [library, setLibrary] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [share, setShare] = useState(null);
  const audioRef = useRef(null);
  const toClip = (a) => ({ path: a.storage_path, url: a.url, duration: a.duration_sec, title: a.title });

  const load = useCallback(async () => {
    try {
      const [{ data: m }, { data: lib }] = await Promise.all([api.get("/ai/voice/voices"), api.get("/ai/voice/audio", { params: { kind: "tts" } })]);
      setMeta(m);
      setLibrary(lib);
    } catch (e) { toast.error("Could not load AI voices"); }
  }, []);

  useEffect(() => { load(); return () => audioRef.current?.pause(); }, [load]);

  const play = (key, url) => {
    if (playing === key) { audioRef.current?.pause(); setPlaying(null); return; }
    audioRef.current?.pause();
    const a = new Audio(fileUrl(url));
    a.onended = () => setPlaying(null);
    a.play().catch(() => toast.error("Playback blocked — tap again"));
    audioRef.current = a;
    setPlaying(key);
  };

  const previewVoice = async (v) => {
    if (v.preview_url) return play(`v-${v.voice_id}`, v.preview_url);
    try {
      const { data } = await api.post(`/ai/voice/voices/${v.voice_id}/preview`);
      setMeta((m) => ({ ...m, voices: m.voices.map((x) => (x.voice_id === v.voice_id ? { ...x, preview_url: data.url } : x)) }));
      play(`v-${v.voice_id}`, data.url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  };

  const generate = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/voice/tts", { text, voice_id: voiceId, title, kind: "tts" });
      setResult(data);
      setLibrary((l) => [data, ...l]);
      setMeta((m) => m && ({ ...m, usage: { ...m.usage, chars_today: m.usage.chars_today + data.chars } }));
      play(`r-${data.id}`, data.url);
      toast.success(`Generated with ${data.voice_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const toLibrary = async (id) => {
    try {
      const { data } = await api.post(`/ai/voice/audio/${id}/to-library`);
      toast.success(data.already_existed ? "Already in your Voice Studio library" : "Saved to Voice Studio library");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/ai/voice/audio/${id}`);
      setLibrary((l) => l.filter((x) => x.id !== id));
      if (result?.id === id) setResult(null);
    } catch (e) { toast.error("Delete failed"); }
  };

  const selected = meta?.voices?.find((v) => v.voice_id === voiceId);
  const maxChars = meta?.limits?.max_chars_per_request || 500;
  const left = Math.max(0, (meta?.limits?.chars_per_day || 0) - (meta?.usage?.chars_today || 0));

  return (
    <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-6" data-testid="ai-voice-studio">
      <div className="flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream" data-testid="aivoice-back"><ArrowLeft size={18} /></button>
        <div>
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Creator Studio · ElevenLabs</p>
          <h1 className="text-cream text-3xl font-black tracking-tighter">AI Voice Studio</h1>
        </div>
      </div>

      {meta && !meta.enabled && (
        <div className="mt-6 rounded-2xl bg-red-500/10 border border-red-500/30 p-4 text-red-200 text-sm">AI voice is not configured yet.</div>
      )}

      {/* Usage meter */}
      {meta && (
        <div className="mt-5 rounded-2xl bg-white/5 border border-white/10 p-3 flex items-center justify-between" data-testid="aivoice-usage">
          <div>
            <p className="text-cream/50 text-[10px] tracking-[0.25em] uppercase font-bold">Today</p>
            <p className="text-cream text-sm font-bold">{meta.usage.chars_today.toLocaleString()} / {meta.limits.chars_per_day.toLocaleString()} characters</p>
          </div>
          <span className={`text-[10px] font-black tracking-[0.2em] uppercase px-2.5 py-1 rounded-full ${meta.is_premium_user ? "bg-jam-gold text-jam-black" : "bg-white/10 text-cream/70"}`}>{meta.is_premium_user ? "Premium" : "Free plan"}</span>
        </div>
      )}

      {/* Text */}
      <section className="mt-6">
        <label className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold block mb-2">Your script</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, maxChars))}
          placeholder="Welcome to Giffwurldaunce! Tonight we dance…"
          rows={4}
          data-testid="aivoice-text"
          className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-papaya text-sm leading-relaxed"
        />
        <div className="flex items-center justify-between mt-1.5">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" data-testid="aivoice-title" className="bg-transparent text-cream/70 text-xs focus:outline-none placeholder:text-cream/30 flex-1" />
          <span className={`text-[11px] tabular-nums ${text.length >= maxChars ? "text-red-300" : "text-cream/40"}`} data-testid="aivoice-charcount">{text.length}/{maxChars}</span>
        </div>
      </section>

      {/* Voice picker */}
      <section className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <label className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold">Voice {selected ? `· ${selected.name}` : ""}</label>
          <span className="text-cream/40 text-[10px]">tap ▶ to hear a sample</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="aivoice-voices">
          {(meta?.voices || []).map((v) => {
            const locked = v.is_premium && !meta.is_premium_user;
            const active = v.voice_id === voiceId;
            return (
              <div
                key={v.voice_id}
                onClick={() => !locked && setVoiceId(v.voice_id)}
                data-testid={`aivoice-voice-${v.voice_id}`}
                aria-pressed={active}
                className={`relative rounded-2xl p-3 border cursor-pointer transition-colors ${active ? "bg-papaya/15 border-papaya" : "bg-white/5 border-white/10 hover:border-white/25"} ${locked ? "opacity-60" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-cream font-black text-sm">{v.name}</p>
                  <button
                    onClick={(e) => { e.stopPropagation(); previewVoice(v); }}
                    data-testid={`aivoice-preview-${v.voice_id}`}
                    className={`w-7 h-7 rounded-full flex items-center justify-center ${playing === `v-${v.voice_id}` ? "bg-papaya text-ink" : "bg-white/10 text-cream"}`}
                  >
                    {playing === `v-${v.voice_id}` ? <Pause size={12} weight="fill" /> : <Play size={12} weight="fill" />}
                  </button>
                </div>
                <p className="text-cream/50 text-[11px] mt-0.5">{v.gender} · {v.accent}</p>
                <p className="text-cream/70 text-[11px] italic">{v.vibe}</p>
                {v.is_premium && (
                  <span className="absolute -top-1.5 right-2 inline-flex items-center gap-0.5 bg-jam-gold text-jam-black text-[8px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded-full">
                    {locked && <Lock size={8} weight="fill" />} Premium
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <button
        onClick={generate}
        disabled={busy || !text.trim() || !meta?.enabled || left <= 0}
        data-testid="aivoice-generate"
        className="mt-6 w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm flex items-center justify-center gap-2 disabled:opacity-40"
      >
        <Sparkle size={18} weight="fill" /> {busy ? "Generating…" : result ? "Regenerate" : "Generate speech"}
      </button>

      {/* Result */}
      {result && (
        <div className="mt-5 rounded-3xl crystal-glass border border-papaya/30 p-4" data-testid="aivoice-result">
          <div className="flex items-center gap-3">
            <button onClick={() => play(`r-${result.id}`, result.url)} data-testid="aivoice-result-play" className={`w-12 h-12 rounded-full flex items-center justify-center ${playing === `r-${result.id}` ? "bg-papaya text-ink" : "bg-white/10 text-cream"}`}>
              {playing === `r-${result.id}` ? <Pause size={20} weight="fill" /> : <Play size={20} weight="fill" />}
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-cream font-black truncate">{result.title}</p>
              <p className="text-cream/50 text-xs">{result.voice_name} · {result.chars} chars · ~{result.duration_sec}s</p>
            </div>
          </div>
          <div className="flex gap-2 mt-3 flex-wrap">
            <button onClick={generate} disabled={busy} data-testid="aivoice-regenerate" className="px-3 py-2 rounded-full bg-white/5 border border-white/15 text-cream text-[10px] font-black tracking-[0.2em] uppercase flex items-center gap-1"><ArrowsClockwise size={12} weight="bold" /> Regenerate</button>
            <button onClick={() => toLibrary(result.id)} data-testid="aivoice-save-library" className="px-3 py-2 rounded-full bg-white/5 border border-white/15 text-cream text-[10px] font-black tracking-[0.2em] uppercase flex items-center gap-1"><FloppyDisk size={12} weight="bold" /> Save to library</button>
            <button onClick={() => setShare(toClip(result))} data-testid="aivoice-share" className="px-3 py-2 rounded-full bg-white/5 border border-white/15 text-cream text-[10px] font-black tracking-[0.2em] uppercase flex items-center gap-1"><PaperPlaneTilt size={12} weight="fill" /> Send as DM</button>
            <Link to={`/create?ai_audio=${result.id}`} data-testid="aivoice-use-in-video" className="px-3 py-2 rounded-full bg-papaya text-ink text-[10px] font-black tracking-[0.2em] uppercase flex items-center gap-1"><FilmSlate size={12} weight="fill" /> Use in a video</Link>
          </div>
        </div>
      )}

      {/* Shortcuts */}
      <div className="mt-8 grid grid-cols-2 gap-2">
        <Link to="/ai-voiceover" data-testid="aivoice-to-voiceover" className="rounded-2xl bg-white/5 border border-white/10 p-3 hover:border-papaya/40 transition-colors">
          <Microphone size={18} className="text-papaya" weight="duotone" />
          <p className="text-cream font-black text-sm mt-1">AI Voiceover</p>
          <p className="text-cream/50 text-[11px]">Narrate an uploaded video</p>
        </Link>
        <Link to="/ai-video" data-testid="aivoice-to-aivideo" className="rounded-2xl bg-white/5 border border-white/10 p-3 hover:border-papaya/40 transition-colors">
          <FilmSlate size={18} className="text-papaya" weight="duotone" />
          <p className="text-cream font-black text-sm mt-1">AI Text-to-Video</p>
          <p className="text-cream/50 text-[11px]">Script → narrated video</p>
        </Link>
        <Link to="/live/go" data-testid="aivoice-to-announcer" className="rounded-2xl bg-white/5 border border-white/10 p-3 hover:border-papaya/40 transition-colors">
          <Megaphone size={18} className="text-papaya" weight="duotone" />
          <p className="text-cream font-black text-sm mt-1">AI Announcer</p>
          <p className="text-cream/50 text-[11px]">Battle & event intros in Go Live</p>
        </Link>
      </div>

      {/* Library */}
      <section className="mt-8">
        <p className="text-cream/50 text-xs tracking-[0.25em] uppercase font-bold mb-2">My AI clips</p>
        {library.length === 0 ? (
          <p className="text-cream/40 text-sm">Nothing yet — generate your first clip above.</p>
        ) : (
          <div className="space-y-2" data-testid="aivoice-library">
            {library.map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/5" data-testid={`aivoice-clip-${a.id}`}>
                <button onClick={() => play(`l-${a.id}`, a.url)} data-testid={`aivoice-clip-play-${a.id}`} className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${playing === `l-${a.id}` ? "bg-papaya text-ink" : "bg-white/10 text-cream"}`}>
                  {playing === `l-${a.id}` ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-cream font-bold text-sm truncate">{a.title}</p>
                  <p className="text-cream/50 text-[11px] truncate">{a.voice_name} · {a.chars} chars</p>
                </div>
                <button onClick={() => setShare(toClip(a))} data-testid={`aivoice-clip-share-${a.id}`} title="Send as DM" className="w-9 h-9 rounded-full bg-white/10 text-cream flex items-center justify-center"><PaperPlaneTilt size={14} weight="fill" /></button>
                <Link to={`/create?ai_audio=${a.id}`} title="Use in a video" className="w-9 h-9 rounded-full bg-white/10 text-cream flex items-center justify-center"><FilmSlate size={14} weight="fill" /></Link>
                <button onClick={() => remove(a.id)} data-testid={`aivoice-clip-delete-${a.id}`} title="Delete" className="w-9 h-9 rounded-full bg-red-500/15 text-red-300 flex items-center justify-center"><TrashSimple size={14} weight="bold" /></button>
              </div>
            ))}
          </div>
        )}
      </section>
      <ShareVoiceSheet clip={share} onClose={() => setShare(null)} />
    </div>
  );
}
